import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Shield,
  CheckCircle,
  AlertTriangle,
  Clock,
  GitCommit,
  FileCode,
  XCircle,
  RotateCcw,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useToast } from '../components/ui/toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface Finding {
  id: string;
  scan_id: string;
  pattern_id: string;
  pattern_name: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  file_path: string;
  line_number: number;
  snippet: string;
  status: 'active' | 'dismissed' | 'fixed';
  dismissed_by?: string;
  dismissed_at?: string;
  dismissed_reason?: string;
  created_at: string;
}

interface Scan {
  id: string;
  project_id: string;
  source: string;
  commit_sha?: string;
  branch?: string;
  findings_count: number;
  created_at: string;
  created_by?: string;
  findings: Finding[];
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getSourceLabel(source: string): string {
  switch (source) {
    case 'cli':
      return 'CLI';
    case 'action':
      return 'GitHub Action';
    case 'app':
      return 'Dashboard';
    default:
      return source;
  }
}

function getSeverityBadgeClasses(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'high':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'low':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  }
}

interface FindingsByFile {
  [filePath: string]: Finding[];
}

export function ScanDetailPage() {
  const { projectId, scanId } = useParams<{ projectId: string; scanId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const {
    data: scan,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['scan', projectId, scanId],
    queryFn: async () => {
      const response = await apiClient.get<Scan>(`/api/v1/projects/${projectId}/scans/${scanId}`);
      return response.data;
    },
    enabled: !!projectId && !!scanId,
    staleTime: 1000 * 30,
  });

  const updateFindingMutation = useMutation({
    mutationFn: async ({
      findingId,
      status,
      reason,
    }: {
      findingId: string;
      status: 'active' | 'dismissed' | 'fixed';
      reason?: string;
    }) => {
      const response = await apiClient.patch<Finding>(
        `/api/v1/projects/${projectId}/scans/${scanId}/findings/${findingId}`,
        { status, reason }
      );
      return response.data;
    },
    onSuccess: (updatedFinding) => {
      // Update the cache
      queryClient.setQueryData(['scan', projectId, scanId], (old: Scan | undefined) => {
        if (!old) return old;
        return {
          ...old,
          findings: old.findings.map((f) =>
            f.id === updatedFinding.id ? updatedFinding : f
          ),
        };
      });
      toast.success(
        updatedFinding.status === 'dismissed'
          ? 'Finding dismissed'
          : 'Finding restored'
      );
    },
    onError: () => {
      toast.error('Failed to update finding');
    },
  });

  // Group findings by file
  const findings = scan?.findings;
  const findingsByFile = useMemo<FindingsByFile>(() => {
    if (!findings) return {};
    return findings.reduce<FindingsByFile>((acc, finding) => {
      if (!acc[finding.file_path]) {
        acc[finding.file_path] = [];
      }
      acc[finding.file_path].push(finding);
      return acc;
    }, {});
  }, [findings]);

  const activeFindings = scan?.findings?.filter((f) => f.status === 'active') ?? [];
  const dismissedFindings = scan?.findings?.filter((f) => f.status === 'dismissed') ?? [];

  if (!projectId || !scanId) {
    return (
      <div className="max-w-6xl mx-auto">
        <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">Invalid scan URL</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="max-w-6xl mx-auto"
    >
      {/* Back navigation */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-6"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/projects/${projectId}/scans`)}
          className="gap-2 hover:bg-muted/50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Scans
        </Button>
      </motion.div>

      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="glass overflow-hidden mb-6">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <div className="h-6 w-48 bg-muted/50 rounded animate-shimmer" />
                    <div className="h-4 w-32 bg-muted/30 rounded animate-shimmer" />
                    <div className="flex gap-4 mt-4">
                      <div className="h-4 w-24 bg-muted/30 rounded animate-shimmer" />
                      <div className="h-4 w-24 bg-muted/30 rounded animate-shimmer" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="glass overflow-hidden">
              <CardContent className="p-0">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-5 border-b border-border/50 last:border-b-0">
                    <div className="h-4 w-48 bg-muted/50 rounded animate-shimmer mb-4" />
                    <div className="space-y-3">
                      <div className="h-16 bg-muted/30 rounded animate-shimmer" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ) : !scan ? (
          <motion.div
            key="not-found"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
              <CardContent className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-muted/30 mb-4">
                  <Shield className="h-8 w-8 text-muted-foreground/50" />
                </div>
                <h3 className="text-lg font-medium text-foreground">Scan not found</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  This scan may have been deleted or you may not have access to it.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Scan metadata */}
            <Card className="glass overflow-hidden mb-6">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2.5 rounded-lg ${
                          activeFindings.length > 0
                            ? 'bg-orange-500/10'
                            : 'bg-green-500/10'
                        }`}
                      >
                        {activeFindings.length > 0 ? (
                          <AlertTriangle className="h-5 w-5 text-orange-500" />
                        ) : (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        )}
                      </div>
                      <div>
                        <h1 className="text-xl font-bold text-foreground">
                          {getSourceLabel(scan.source)} Scan
                        </h1>
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                          {scan.commit_sha && (
                            <span className="flex items-center gap-1 font-mono">
                              <GitCommit className="h-3.5 w-3.5" />
                              {scan.commit_sha.slice(0, 7)}
                            </span>
                          )}
                          {scan.branch && <span>on {scan.branch}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Shield className="h-4 w-4" />
                        <span>
                          {activeFindings.length === 0 ? (
                            <span className="text-green-600">No active findings</span>
                          ) : (
                            <span className="text-orange-600">
                              {activeFindings.length} active{' '}
                              {activeFindings.length === 1 ? 'finding' : 'findings'}
                            </span>
                          )}
                        </span>
                        {dismissedFindings.length > 0 && (
                          <span className="text-muted-foreground">
                            ({dismissedFindings.length} dismissed)
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{formatDateTime(scan.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    title="Refresh"
                    className="hover:bg-muted/50"
                  >
                    <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Findings grouped by file */}
            {Object.keys(findingsByFile).length === 0 ? (
              <Card className="glass overflow-hidden">
                <CardContent className="py-16 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-500/10 mb-4">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground">All clear!</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    No secrets were detected in this scan.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {Object.entries(findingsByFile).map(([filePath, findings], fileIndex) => (
                  <motion.div
                    key={filePath}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: fileIndex * 0.05 }}
                  >
                    <Card className="glass overflow-hidden">
                      <div className="px-5 py-3 bg-muted/20 border-b border-border/50">
                        <div className="flex items-center gap-2">
                          <FileCode className="h-4 w-4 text-muted-foreground" />
                          <span className="font-mono text-sm text-foreground">
                            {filePath}
                          </span>
                          <Badge variant="secondary" className="ml-2">
                            {findings.length} {findings.length === 1 ? 'finding' : 'findings'}
                          </Badge>
                        </div>
                      </div>

                      <div className="divide-y divide-border/50">
                        {findings.map((finding, findingIndex) => (
                          <motion.div
                            key={finding.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: (fileIndex * 0.05) + (findingIndex * 0.02) }}
                            className={`p-5 ${
                              finding.status === 'dismissed' ? 'opacity-60' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge
                                    className={`font-medium ${getSeverityBadgeClasses(
                                      finding.severity
                                    )}`}
                                  >
                                    {finding.severity.toUpperCase()}
                                  </Badge>
                                  <span className="font-medium text-foreground">
                                    {finding.pattern_name}
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    Line {finding.line_number}
                                  </span>
                                  {finding.status === 'dismissed' && (
                                    <Badge variant="outline" className="text-muted-foreground">
                                      Dismissed
                                    </Badge>
                                  )}
                                </div>

                                {finding.snippet && (
                                  <div className="mt-3">
                                    <pre className="bg-muted/30 rounded-lg p-3 text-sm font-mono overflow-x-auto">
                                      <code className="text-muted-foreground">
                                        {finding.snippet}
                                      </code>
                                    </pre>
                                  </div>
                                )}

                                {finding.dismissed_reason && (
                                  <p className="mt-2 text-sm text-muted-foreground italic">
                                    Reason: {finding.dismissed_reason}
                                  </p>
                                )}
                              </div>

                              <div className="ml-4 flex-shrink-0">
                                {finding.status === 'active' ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      updateFindingMutation.mutate({
                                        findingId: finding.id,
                                        status: 'dismissed',
                                      })
                                    }
                                    disabled={updateFindingMutation.isPending}
                                    className="gap-1.5 text-muted-foreground hover:text-foreground"
                                  >
                                    <XCircle className="h-4 w-4" />
                                    Dismiss
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      updateFindingMutation.mutate({
                                        findingId: finding.id,
                                        status: 'active',
                                      })
                                    }
                                    disabled={updateFindingMutation.isPending}
                                    className="gap-1.5 text-muted-foreground hover:text-foreground"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                    Restore
                                  </Button>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
